if (!customElements.get('gwp-gift')) {
  // One request per gift and storefront language: the cart drawer re-renders its
  // markup on every change, and re-fetching each time would make the gift flicker.
  const resolved = new Map();
  const pending = new Map();

  // Cache key per gift and language; /en/variants/<id> resolves to the English product.
  const giftUrl = (variantId) =>
    `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}variants/${variantId}`;

  // A gift variant the storefront doesn't sell (404: unpublished, draft or deleted).
  // Regios can't add it to the cart either, so no display promises it.
  const MISSING = { missing: true };

  // /variants/<id> redirects to /products/<handle>?variant=<id>, but only without
  // section_id — with it Shopify renders the section on the variant URL, without a
  // product. So resolve the product URL first, then render gwp-gift there.
  const sectionUrl = (variantUrl) =>
    fetch(variantUrl, { method: 'HEAD' }).then((response) => {
      if (response.status === 404) return null;
      const target = new URL(response.url);
      if (!response.ok || !target.pathname.includes('/products/')) {
        throw new Error(`GWP gift ${variantUrl}: ${response.status}`);
      }
      target.searchParams.set('section_id', 'gwp-gift');
      return target.toString();
    });

  const readStored = (url) => {
    try {
      const value = sessionStorage.getItem(`gwp-gift:${url}`);
      return value ? JSON.parse(value) : undefined;
    } catch (error) {
      return undefined;
    }
  };

  const store = (url, data) => {
    try {
      sessionStorage.setItem(`gwp-gift:${url}`, JSON.stringify(data));
    } catch (error) {
      // Storage full or blocked: the in-memory cache still covers this page view.
    }
  };

  const loadGift = (variantId) => {
    const url = giftUrl(variantId);
    if (resolved.has(url)) return Promise.resolve(resolved.get(url));
    if (pending.has(url)) return pending.get(url);

    const stored = readStored(url);
    if (stored !== undefined) {
      resolved.set(url, stored);
      return Promise.resolve(stored);
    }

    const request = sectionUrl(url)
      .then((target) => {
        if (!target) return MISSING;
        return fetch(target)
          .then((response) => {
            if (!response.ok) throw new Error(`GWP gift ${variantId}: ${response.status}`);
            return response.text();
          })
          .then((html) => {
            const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-gwp-gift]');
            return node ? JSON.parse(node.textContent) : null;
          });
      })
      .then((data) => {
        resolved.set(url, data);
        // A missing gift is only remembered for this page view: it may be published later.
        if (data && !data.missing) store(url, data);
        return data;
      })
      .finally(() => pending.delete(url));

    pending.set(url, request);
    return request;
  };

  class GwpGift extends HTMLElement {
    connectedCallback() {
      const { variantId } = this.dataset;
      if (!variantId) return;

      // A cached gift is applied synchronously, before the re-rendered cart paints.
      const cached = resolved.get(giftUrl(variantId));
      if (cached !== undefined) {
        this.render(cached);
        return;
      }

      loadGift(variantId)
        .then((data) => this.render(data))
        .catch(() => this.render(null));
    }

    render(data) {
      if (data && data.missing) {
        // A reached cart tier would say "Geschafft" for a gift that never arrives.
        // An upcoming one keeps its threshold with the generic "Bestellgeschenk" title.
        if (!this.hasAttribute('data-keep-on-error') || this.hasAttribute('data-done')) this.hidden = true;
        return;
      }
      // Sold out: the Bestellgeschenke page shows only gifts that can still be given.
      if (data && data.available === false && this.hasAttribute('data-hide-unavailable')) {
        this.hidden = true;
        return;
      }
      if (!data) {
        // Cart bars keep their server-rendered fallback title: the threshold still
        // matters to the customer. The promo box would be an empty promise.
        if (!this.hasAttribute('data-keep-on-error')) this.hidden = true;
        return;
      }

      this.querySelectorAll('[data-gwp-title]').forEach((element) => {
        element.textContent = data.title;
      });

      // The description is inline HTML rendered by gwp-gift.liquid from the gift's
      // rich text metafield (bold brand names etc.), the same kind of markup as the
      // server-rendered condition text it replaces.
      if (data.description) {
        this.querySelectorAll('[data-gwp-description]').forEach((element) => {
          element.innerHTML = data.description;
        });
      }

      if (data.url) {
        this.querySelectorAll('[data-gwp-link]').forEach((element) => {
          element.setAttribute('href', data.url);
        });
      }

      const imageSlot = this.querySelector('[data-gwp-image]');
      if (imageSlot && data.image && !imageSlot.querySelector('img')) {
        const image = document.createElement('img');
        image.src = data.image.src;
        image.srcset = data.image.srcset;
        image.sizes = imageSlot.dataset.sizes || this.dataset.imageSizes || '90px';
        image.alt = data.image.alt || '';
        image.loading = 'lazy';
        if (data.image.width) image.width = data.image.width;
        if (data.image.height) image.height = data.image.height;
        imageSlot.append(image);
      }

      this.classList.add('is-loaded');
    }
  }

  customElements.define('gwp-gift', GwpGift);
}
